import { coordKey, getNeighbors } from "../../hex-path/hex-grid";
import { hexCellCenter, type HexagonLayoutMetrics } from "./hexagon-layout-metrics";
import {
	getHexagonPathTileAppearance,
	resolveHexagonPathTileVariant,
} from "./hexagon-path-tile-appearance";

export interface PathConnectorCellInput {
	key: string;
	row: number;
	col: number;
	pathIndex: number;
	isCompleted: boolean;
}

export interface PathConnectorSegment {
	key: string;
	fromKey: string;
	toKey: string;
	fromPathIndex: number;
	toPathIndex: number;
	x1: number;
	y1: number;
	x2: number;
	y2: number;
	strokeColor: string;
	/** Locked endpoint → match path-tile locked opacity (0.5). */
	opacity: number;
}

/** Matches `LOCKED_PATH_TILE_OPACITY` on path tiles. */
const LOCKED_CONNECTOR_OPACITY = 0.5;

/**
 * Center-to-center connectors for consecutive path indices that are hex neighbors.
 * Stroke color from the **earlier** tile; opacity dims if either endpoint is locked.
 */
export function buildPathConnectorSegments(input: {
	cells: readonly PathConnectorCellInput[];
	metrics: HexagonLayoutMetrics;
	activePlayerCoord: { row: number; col: number } | null;
	lockedPathCoordKeys?: ReadonlySet<string>;
}): PathConnectorSegment[] {
	const { cells, metrics, activePlayerCoord, lockedPathCoordKeys } = input;
	const byKey = new Map<string, PathConnectorCellInput>();
	for (const cell of cells) {
		if (cell.pathIndex < 0) {
			continue;
		}
		byKey.set(cell.key, cell);
	}

	const segments: PathConnectorSegment[] = [];
	for (const from of byKey.values()) {
		const fromActive =
			activePlayerCoord != null &&
			activePlayerCoord.row === from.row &&
			activePlayerCoord.col === from.col;
		const appearance = getHexagonPathTileAppearance(
			resolveHexagonPathTileVariant({
				isActive: fromActive,
				isCompleted: from.isCompleted,
			}),
		);
		const fromCenter = hexCellCenter(from.row, from.col, metrics);
		const fromLocked = lockedPathCoordKeys?.has(from.key) === true;

		for (const neighbor of getNeighbors(from.row, from.col)) {
			const to = byKey.get(coordKey(neighbor.row, neighbor.col));
			if (!to || to.pathIndex !== from.pathIndex + 1) {
				continue;
			}
			const toCenter = hexCellCenter(to.row, to.col, metrics);
			const toLocked = lockedPathCoordKeys?.has(to.key) === true;
			segments.push({
				key: `${from.key}->${to.key}`,
				fromKey: from.key,
				toKey: to.key,
				fromPathIndex: from.pathIndex,
				toPathIndex: to.pathIndex,
				x1: fromCenter.left,
				y1: fromCenter.top,
				x2: toCenter.left,
				y2: toCenter.top,
				strokeColor: appearance.strokeColor,
				opacity:
					fromLocked || toLocked ? LOCKED_CONNECTOR_OPACITY : 1,
			});
		}
	}

	segments.sort((a, b) => a.toPathIndex - b.toPathIndex);
	return segments;
}
